/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { Root as LabelPrimitiveRoot } from '@radix-ui/react-label';

import { cn } from '@/lib/utils';

type LabelProps = ComponentPropsWithoutRef<typeof LabelPrimitiveRoot>;

const Label = forwardRef<ElementRef<typeof LabelPrimitiveRoot>, LabelProps>(({ className, ...props }, ref) => {
    return (
        <LabelPrimitiveRoot
            ref={ref}
            data-slot="label"
            className={cn(
                'flex items-center gap-2 text-sm leading-5 font-semibold text-foreground select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
                className
            )}
            {...props}
        />
    );
});

Label.displayName = LabelPrimitiveRoot.displayName;

export { Label };
